// src/pages/Login.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { axiosClient } from "../core/api/axiosClient";
import { tokenStorage } from "../core/storage/tokenStorage";

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();

  const [username, setUsername] = useState(""); // could be email/username
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const [error, setError] = useState("");
  const [info, setInfo] = useState("");

  const from = location.state?.from || "/dashboard";

  const query = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const reason = query.get("reason");

  useEffect(() => {
    if (reason === "expired") {
      setInfo("Session expired, please login again.");
    } else {
      setInfo("");
    }
  }, [reason]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setInfo("");

    if (!username.trim() || !password) {
      setError("Please enter username/email and password.");
      return;
    }

    try {
      setIsLoading(true);

      // ✅ Example payload (adjust key if backend differs)
      const payload = {
        username: username.trim(),
        password,
      };

      const res = await axiosClient.post("/users/login/", payload);

      // ✅ Expected response handling:
      // Common patterns:
      // A) { "access": "...", "refresh": "..." }
      // B) { "tokens": { "access": "...", "refresh": "..." } }
      // C) { "access_token": "...", "refresh_token": "..." }
      const data = res?.data || {};

      const access =
        data?.access ||
        data?.tokens?.access ||
        data?.access_token ||
        data?.token?.access; // fallback

      const refresh =
        data?.refresh ||
        data?.tokens?.refresh ||
        data?.refresh_token ||
        data?.token?.refresh;

      if (!access) {
        setError(
          "Login succeeded but access token not found in response. Please adjust token keys in Login.jsx."
        );
        return;
      }

      tokenStorage.setTokens({ access, refresh });

      // Redirect to dashboard (or intended route)
      navigate(from, { replace: true });
    } catch (err) {
      const status = err?.response?.status;

      if (status === 400 || status === 401) {
        setError("Invalid credentials. Please try again.");
      } else if (!err?.response) {
        setError("Server not reachable. Please check backend is running.");
      } else {
        setError("Something went wrong. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-64px)] bg-gradient-to-br from-primary/10 via-accent to-primary/20 flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md">
        {/* Tag pill (same style family as Home.jsx) */}
        <div className="flex justify-center sm:justify-start mb-4">
          <span
            className="
              inline-flex items-center gap-2
              px-4 py-1.5
              rounded-full
              border border-secondary
              bg-white/70 backdrop-blur-sm
              text-secondary
              text-sm font-semibold
              shadow-sm
              tracking-wide
            "
          >
            <span className="h-2 w-2 rounded-full bg-secondary" />
            Admin / Team Login
          </span>
        </div>

        {/* Card */}
        <div className="bg-white/80 backdrop-blur-sm border border-secondary rounded-2xl shadow-lg p-6 sm:p-7">
          <h1 className="text-2xl sm:text-3xl font-bold text-secondary">
            Welcome back
          </h1>
          <p className="mt-2 text-secondary/80">
            Sign in to continue to your dashboard.
          </p>

          {info ? (
            <div className="mt-4 rounded-xl border border-secondary/40 bg-white p-3 text-secondary text-sm">
              {info}
            </div>
          ) : null}

          {error ? (
            <div className="mt-4 rounded-xl border border-red-300 bg-red-50 p-3 text-red-700 text-sm">
              {error}
            </div>
          ) : null}

          <form className="mt-6 space-y-4" onSubmit={onSubmit}>
            <div>
              <label className="block text-sm font-semibold text-secondary">
                Username / Email
              </label>
              <input
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoComplete="username"
                placeholder="Enter username or email"
                className="
                  mt-2 w-full rounded-xl
                  border border-secondary/40
                  bg-white
                  px-4 py-3
                  text-secondary
                  outline-none
                  focus:border-secondary
                  focus:ring-2 focus:ring-secondary/20
                "
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-secondary">
                Password
              </label>
              <input
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                type="password"
                autoComplete="current-password"
                placeholder="Enter password"
                className="
                  mt-2 w-full rounded-xl
                  border border-secondary/40
                  bg-white
                  px-4 py-3
                  text-secondary
                  outline-none
                  focus:border-secondary
                  focus:ring-2 focus:ring-secondary/20
                "
              />
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="
                w-full
                rounded-xl
                bg-secondary
                text-accent
                px-5 py-3
                font-semibold
                shadow-md
                transition-all duration-300
                hover:bg-primary hover:text-secondary
                disabled:opacity-60 disabled:cursor-not-allowed
              "
            >
              {isLoading ? "Signing in..." : "Sign In"}
            </button>

            <p className="text-xs text-secondary/70">
              Tip: Press <b>Enter</b> to submit.
            </p>
          </form>
        </div>

        {/* Small footer text */}
        <p className="mt-4 text-center text-xs text-secondary/70">
          © 2025 MockingJay.tech
        </p>
      </div>
    </div>
  );
}
